import { Page } from "entities/pages/types";
import { Link } from "react-router-dom";
import Chip from "shared/components/Chip";
import { Column } from "shared/components/DragNDropTable/helpers";
import DropdownList, {
  DropdownListItemProps,
} from "shared/components/DropdownList";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import { COLOR_BLACK, COLOR_PRIMARY_MAIN } from "shared/theme/colors";
import { ArrowForwardIcon } from "shared/theme/icons/ArrowForwardIcon";
import { formatUpdatedAt } from "shared/utils/formatUpdatedAt";
import AuthBadge from "./components/AuthBadge";
import MaintenanceModeBadge from "./components/MaintenanceModeBadge";
import { StyledActionWrapper } from "./styled";
import { PagesColumnsProps } from "./types";
import { getChipColor } from "./utils";

const renderStatusCell = (item: Page) => {
  const color = getChipColor(item.isActive);
  const title = Boolean(item.isActive) ? "Active" : "Inactive";

  return <Chip title={title} bgColor={color} />;
};

const renderModuleName = (item: Page, currentLocation: string) => {
  const hasAuthenticated = item.isDefaultForAuthenticated;
  const hasUnauthenticated = item.isDefaultForUnauthenticated;

  return (
    <FlexCenter columnGap="0.75rem">
      <Link
        to={{
          pathname: `${currentLocation}/${item.id}`,
        }}
        style={{ color: COLOR_BLACK }}
      >
        {item.title}
      </Link>
      {hasAuthenticated && <AuthBadge title={"Authenticated"} />}
      {hasUnauthenticated && <AuthBadge title={"Unauthenticated"} />}
      {item.isMaintenanceMode && (
        <MaintenanceModeBadge description={item.maintenanceModeDescription} />
      )}
    </FlexCenter>
  );
};

export const getPagesColumns = ({
  currentLocation,
  handlePublishStatus,
  handleOpenDeleteModal,
  pageDrawer,
  handleAuthenticatedPage,
  handleUnauthenticatedPage,
  handlePageMaintenanceMode,
  handleOpenPageMaintenanceModeModal,
  readOnly,
  isAdmin,
}: PagesColumnsProps): Column<Page>[] => {
  const renderActions = (page: Page) => {
    const { isActive, id: pageId, isMaintenanceMode } = page;

    if (readOnly) {
      return (
        <StyledActionWrapper onClick={() => pageDrawer.openDrawer(pageId)}>
          <ArrowForwardIcon color={COLOR_PRIMARY_MAIN} />
        </StyledActionWrapper>
      );
    }

    const statusTitle = isActive ? "Unpublish" : "Publish";

    const hasShowAuthorized = !page.isDefaultForAuthenticated;
    const hasShowUnauthorized = !page.isDefaultForUnauthenticated;

    const items = [
      {
        title: "Edit",
        onClick: () => pageDrawer.openDrawer(pageId),
      },
      hasShowAuthorized
        ? {
            title: "Default (Authenticated)",
            style: "normal",
            onClick: () => handleAuthenticatedPage(pageId),
          }
        : null,
      hasShowUnauthorized
        ? {
            title: "Default (Unauthenticated)",
            style: "normal",
            onClick: () => handleUnauthenticatedPage(pageId),
          }
        : null,
      isAdmin
        ? {
            title: isMaintenanceMode
              ? "Disable Maintenance Mode"
              : "Enable Maintenance Mode",
            style: "normal",
            onClick: () =>
              isMaintenanceMode
                ? handlePageMaintenanceMode(pageId, !isMaintenanceMode)
                : handleOpenPageMaintenanceModeModal(page),
          }
        : null,
      {
        title: statusTitle,
        onClick: () => {
          handlePublishStatus({ ...page, isActive: !isActive });
        },
      },
      {
        title: "Delete",
        style: "remove",
        onClick: () => handleOpenDeleteModal(page),
      },
    ].filter((item) => item !== null) as DropdownListItemProps[];

    return <DropdownList items={items} />;
  };

  return [
    {
      headerName: "Page name",
      align: "left",
      headerAlign: "left",
      width: "30%",
      renderCell: (item: Page) => renderModuleName(item, currentLocation),
    },
    {
      headerName: "Modules",
      width: "25%",
      align: "left",
      headerAlign: "left",
      renderCell: (item: Page) => item.modulesCount,
    },
    {
      headerName: "Updated at",
      width: "25%",
      align: "left",
      headerAlign: "left",
      renderCell: (item: Page) => formatUpdatedAt(item.updatedAt),
    },
    {
      headerName: "Status",
      width: "25%",
      align: "left",
      headerAlign: "left",
      renderCell: renderStatusCell,
    },
    {
      headerName: "",
      width: "11.25%",
      align: "right",
      headerAlign: "right",
      renderCell: (item: Page) => {
        return renderActions(item);
      },
    },
  ];
};
